<template>
  <div class="main-container" ref="container">

    <div class="recording-card section-card" :style="{ flex: '0 0 ' + recordingWidth + 'px', maxWidth: recordingWidth + 'px' }">
      <div class="section-header">
        <h3 class="section-subtitle">Screen Recording</h3>
      </div>

      <div class="video-container">
        <video
          ref="recordedVideo"
          class="video-frame"
          v-if="video"
          controls
          :src="video"
          @loadedmetadata="onLoadedMetadata"
          @timeupdate="onTimeUpdate"
        ></video>
        <div v-else class="placeholder-overlay">
          <span>No Recording Yet</span>
        </div>
      </div>

      <div v-if="video" class="timeline-container">
        <div class="time-labels">
          <span>{{ formatTime(currentTime) }}</span>
          <span>{{ formatTime(videoDuration) }}</span>
        </div>

        <div class="timeline-track-wrapper">
          <input
            type="range"
            min="0"
            :max="videoDuration"
            step="0.01"
            v-model="currentTime"
            @input="seekVideo"
            class="timeline-slider"
          />

          <div
            class="drop-container visual-timeline"
            @drop="onDrop($event, 3)"
            @dragenter.prevent
            @dragover.prevent
          >
            <p v-if="getInstructions(3).length === 0" class="drop-hint">
              Drag instructions or bugs here to pin them to the timeline
            </p>

            <div
              v-for="item in getInstructions(3)"
              :key="item.id"
              class="timeline-marker"
              :style="{ left: (item.timeStamp / videoDuration * 100) + '%' }"
              @click="currentTime = item.timeStamp; seekVideo()"
            >
              <div class="marker-pin" :class="{ 'pin-bug': item.OGList === 2 }"></div>
              <div class="marker-tooltip">
                <strong>{{ formatTime(item.timeStamp) }}</strong>: {{ item.instruction || 'Untitled' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="recording-actions">
        <button class="action-button btn-start" @click="startRecording()">
          ● Start New Recording
        </button>
        <button class="action-button btn-stop" :disabled="isDisabled" @click="stopRecording()">
          ■ Stop &amp; Save
        </button>
      </div>
    </div>

    <!-- Draggable divider -->
    <div
      class="resizer"
      @mousedown="startResize"
    >
      <div class="resizer-handle"></div>
    </div>

    <div class="drag-drop-card section-card">
      <div class="section-header">
        <h3 class="section-subtitle">Session Notes</h3>
      </div>

      <div class="tab-bar">
        <button
          :class="['tab-btn', { 'tab-active': selectedTab === 'instructions' }]"
          @click="selectTab('instructions')"
        >Instructions</button>
        <button
          :class="['tab-btn', { 'tab-active': selectedTab === 'bugs' }]"
          @click="selectTab('bugs')"
        >Notes</button>
        <button
          :class="['tab-btn', { 'tab-active': selectedTab === 'webcam' }]"
          @click="selectTab('webcam')"
        >Webcam</button>
      </div>

      <div
        v-if="selectedTab === 'instructions'"
        class="tab-content"
        @drop="onDrop($event, 1)"
        @dragenter.prevent
        @dragover.prevent
      >
        <p v-if="getInstructions(1).length === 0" class="drop-hint">No instructions available.</p>
        <div
          v-for="item in getInstructions(1)"
          :key="item.id"
          class="drag-item"
          :class="{ 'drag-item-required': item.required }"
          draggable="true"
          @dragstart="startDrag($event, item)"
        >
          <span class="item-title">{{ item.instruction }}</span>
        </div>
      </div>

      <div
        v-else-if="selectedTab === 'bugs'"
        class="tab-content"
        @drop="onDrop($event, 2)"
        @dragenter.prevent
        @dragover.prevent
      >
        <p v-if="getInstructions(2).length === 0" class="drop-hint">No notes logged yet.</p>
        <div
          v-for="item in getInstructions(2)"
          :key="item.id"
          class="drag-item"
          draggable="true"
          @dragstart="startDrag($event, item)"
        >
          <template v-if="item.isCustom">
            <input class="bug-input bug-title" v-model="item.title" placeholder="Note title" />
            <input class="bug-input bug-desc" v-model="item.instruction" placeholder="Write a note down for a part of the video..." />
          </template>
          <template v-else>
            <span class="item-title">{{ item.title }}</span>
            <span class="item-body">{{ item.instruction }}</span>
          </template>
        </div>

        <div class="bug-actions">
          <button class="action-button btn-add" @click="addBug">+ Add Bug</button>
          <button class="action-button btn-remove" @click="removeBug">− Remove</button>
        </div>
      </div>

      <div v-else class="tab-content webcam-tab">
        <div class="video-container webcam-container">
          <video v-if="webcam" controls :src="webcam" class="video-frame"></video>
          <div v-else class="placeholder-overlay"><span>No Webcam Recording</span></div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import RecordingComponent from './Recording.js';
  import { ref, getCurrentInstance, onMounted } from 'vue';
  import GameTesting from '../../Firestore/GameTesting.js';
  import {db} from '../../db.js';

  export default {
    name: "PlaytestMain",
    data() {
      return {
        selectedTab: 'instructions',
        video: JSON.parse(window.localStorage.getItem('savedVideoRecording'))
          ? JSON.parse(window.localStorage.getItem('savedVideoRecording'))[0] : null,
        webcam: JSON.parse(window.localStorage.getItem('savedWebcamRecording'))
          ? JSON.parse(window.localStorage.getItem('savedWebcamRecording')) : null,
        isDisabled: true,
        isRecording: false,
        videoDuration: JSON.parse(window.localStorage.getItem('savedVideoRecording'))
          ? JSON.parse(window.localStorage.getItem('savedVideoRecording'))[1] : 0,
        currentTime: 0,
        duration: 0,
        recordingWidth: null,
        isResizing: false,
        resizeStartX: 0,
        resizeStartWidth: 0,
      };
    },
    setup(props, { emit }) {
      var bugs  = ref([]);
      var items = ref([]);

      var instrItems = [];
      var bugItems   = [];

      let fetchedInstruction = [];
      let origInstr = [];

      async function fetchInstructions(){
        try {
          const temp = await db.collection('playPeriod').doc(window.localStorage.getItem("playPeriodID")).get();
          fetchedInstruction = temp;
          return fetchedInstruction.exists ? fetchedInstruction.data().instructions : [];
        } catch (error) {
          
        }
      }
      async function loadInstructions(){
        try {
          let savedInstructions = await fetchInstructions();
          if (savedInstructions.length > 0) {
            instrItems = savedInstructions.map((text, i) => ({
              id: i, title: 'instruction' + (i + 1), instruction: text.instructionData, timeStamp: '', OGList: 1, list: 1, required: text.required || false
            }));
          }
        } catch (error) {
        }
      }

      const savedBugs = JSON.parse(window.localStorage.getItem('savedBugs') || '[]');
      if (savedBugs.length > 0) {
        bugItems = savedBugs;
      } else {
        const defaultBugs = [
          { title: '', instruction: '', timeStamp: '', OGList: 2, list: 2, isCustom: true }
        ];
        bugItems = defaultBugs.map((bug, j) => Object.assign({ id: instrItems.length + j, timeStamp: '' }, bug));
      }

      async function saveOrignalInstructions(){
        let savedInstructions = await fetchInstructions();
        return savedInstructions.map((text, i) => ({
          id: i, title: 'instruction' + (i + 1), instruction: text.instructionData, timeStamp: '', OGList: 1, list: 1, required: text.required || false
        }));
      };

      async function buildItems() {
        await loadInstructions();
        items.value = instrItems.concat(bugItems);

        origInstr = await saveOrignalInstructions();
        var origBugs = [
          { title: '', instruction: '', timeStamp: '', OGList: 2, list: 2, isCustom: true }
        ].map(function(bug, j) {
          return Object.assign({ id: origInstr.length + j, timeStamp: '' }, bug);
        });
        originalList.value = origInstr.concat(origBugs);
      };

      var originalList = ref([]);

      onMounted(function() {
        buildItems();
      });

      var instance = getCurrentInstance();

      var getInstructions = function(list) {
        var filtered = items.value.filter(function(item) { return item.list === list; });
        if (list === 3) {
          return filtered.sort(function(a, b) { return a.timeStamp - b.timeStamp; });
        }
        return filtered;
      };

      var resetLists = function() {
        items.value = originalList.value.map(function(i) { return Object.assign({}, i); });
        emit("update_player_items", getInstructions(1).slice(), 0);
        emit("update_player_items", getInstructions(2).slice(), 1);
        emit("disable_submit_on_new_recording")
      };

      var addBug = function() {
        if (items.value.filter(function(i) { return i.list === 2 && i.isCustom; }).length < 2) {
          var newId  = Math.max.apply(null, items.value.map(function(i) { return i.id; })) + 1;
          items.value.push({ id: newId, title: '', instruction: '', timeStamp: '', OGList: 2, list: 2, isCustom: true });
        }
      };

      var removeBug = function() {
        if (items.value.filter(function(i) { return i.list === 2 && i.isCustom; }).length > 1) {
          var idx = items.value.findIndex(function(i) { return i.list === 2 && i.isCustom; });
          if (idx !== -1) items.value.splice(idx, 1);
        }
      };

      var startDrag = function(event, item) {
        event.dataTransfer.dropEffect    = 'move';
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('itemID', item.id);
      };

      var onDrop = function(event, list) {
        var itemID = event.dataTransfer.getData('itemID');
        var item   = items.value.find(function(i) { return i.id == itemID; });
        if (!item) return;

        if (list === 1 && item.OGList === 1) {
          item.list = list;
          var newInstr = getInstructions(3).filter(function(i) { return i.OGList === 1; });
          emit('update_player_items', getInstructions(1).concat(newInstr), 0);

        } else if (list === 2 && item.OGList === 2) {
          var customCount = items.value.filter(function(i) { return i.list === 2 && i.isCustom; }).length;
          if (item.isCustom && customCount >= 2) return;
          item.list = list;
          var newBugs = getInstructions(3).filter(function(i) { return i.OGList === 2; });
          emit('update_player_items', getInstructions(2).concat(newBugs), 1);

        } else if (list === 3) {
          item.list      = list;
          item.timeStamp = instance.proxy.currentTime;

          //if item is dropped into timeline check if there are any instructions that are required still not dropped on timeline
          const hasRequired = getInstructions(1).some(item => item.required);
          if (!hasRequired){
            emit('timeline_item_dropped');
          }
          
          if (item.OGList === 1) {
            var ti = getInstructions(3).filter(function(i) { return i.OGList === 1; });
            emit('update_player_items', getInstructions(1).concat(ti), 0);
          } else if (item.OGList === 2) {
            var tb = getInstructions(3).filter(function(i) { return i.OGList === 2; });
            emit('update_player_items', getInstructions(2).concat(tb), 1);
          }
        }
      };

      return { addBug, removeBug, getInstructions, onDrop, startDrag, resetLists };
    },
    mounted() {
      // Set initial recording width to 55% of container
      this.$nextTick(function() {
        var containerWidth = this.$refs.container ? this.$refs.container.offsetWidth : 800;
        this.recordingWidth = Math.floor(containerWidth * 0.55);
      });
    },
    beforeDestroy() {
      this.stopResize();
    },
    methods: {
      onLoadedMetadata(event) { this.duration = event.target.duration; },
      onTimeUpdate(event) { this.currentTime = event.target.currentTime; },
      selectTab(tab) { this.selectedTab = tab; },
      formatTime(seconds) {
        if (!seconds) return "00:00";
        const m = Math.floor(seconds / 60).toString().padStart(2, "0");
        const s = Math.floor(seconds % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
      },
      seekVideo() {
        const vid = this.$refs.recordedVideo;
        if (vid) vid.currentTime = this.currentTime;
      },
      startResize(event) {
        this.isResizing = true;
        this.resizeStartX = event.clientX;
        this.resizeStartWidth = this.recordingWidth;
        document.addEventListener('mousemove', this.doResize);
        document.addEventListener('mouseup', this.stopResize);
        // Prevent text selection while dragging
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
      },
      doResize(event) {
        if (!this.isResizing) return;
        var containerWidth = this.$refs.container ? this.$refs.container.offsetWidth : 800;
        var minWidth = 280;
        var maxWidth = containerWidth - 280 - 12; // 280 min for notes, 12 for resizer
        var delta = event.clientX - this.resizeStartX;
        var newWidth = this.resizeStartWidth + delta;
        this.recordingWidth = Math.min(maxWidth, Math.max(minWidth, newWidth));
      },
      stopResize() {
        this.isResizing = false;
        document.removeEventListener('mousemove', this.doResize);
        document.removeEventListener('mouseup', this.stopResize);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      },
      async startRecording() {
        this.resetLists();
        if (!this.screenRecorder) this.screenRecorder = new RecordingComponent();
        try {
          await this.screenRecorder.startRecording();
          this.isRecording = true;
          this.isDisabled = false;
        } catch (error) {
          console.error('Failed to start recording:', error);
        }
      },
      async stopRecording() {
        try {
          const blobs = await this.screenRecorder.stopRecording();
          if (!blobs) return;
          
          const gameID       = window.localStorage.getItem('gameId');
          const testerID     = window.localStorage.getItem("userId");
          const periodID     = window.localStorage.getItem('playPeriodID');

          let videoUrl = "", webcamUrl = "";
          let count = 0, total = (blobs.screenBlob ? 1 : 0) + (blobs.webcamBlob ? 1 : 0);

          const done = () => {
            if (++count === total) {
              this.videoDuration = this.screenRecorder.getRecordingDuration();
              this.$emit("recording_finished", videoUrl, this.videoDuration, webcamUrl, this.screenRecorder.transcriptFinal);
            }
          };

          if (blobs.screenBlob) {
            this.video = URL.createObjectURL(blobs.screenBlob);
            const file = new File([blobs.screenBlob], 'screenRecording.mp4', { type: 'video/mp4' });
            await GameTesting.saveVideoRecordingUrl(file, gameID, periodID, testerID);
            videoUrl = await GameTesting.getVideoRecordingUrl(file, gameID, periodID, testerID);
            done();
          }
          if (blobs.webcamBlob) {
            this.webcam = URL.createObjectURL(blobs.webcamBlob);
            const file = new File([blobs.webcamBlob], 'webcamRecording.mp4', { type: 'video/mp4' });
            await GameTesting.saveWebcamRecordingUrl(file, gameID, periodID, testerID);
            webcamUrl = await GameTesting.getWebcamRecordingUrl(file, gameID, periodID, testerID);
            done();
          }
          this.isDisabled = true;
        } catch (error) {
          console.error('Stop recording error:', error);
        } finally {
          this.isRecording = false;
        }
      }
    }
  };
</script>

<style scoped>
/* ── Layout ── */
.main-container {
  display: flex;
  gap: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  align-items: stretch;
  overflow: hidden;
}

.section-card {
  background: white;
  border-radius: 14px;
  padding: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  overflow: hidden;
}

/* Recording card: width controlled by JS resizer */
.recording-card {
  flex-shrink: 0;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Notes card: fills ALL remaining space */
.drag-drop-card {
  flex: 1;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

/* ── Resizer ── */
.resizer {
  flex-shrink: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
  transition: background 0.15s;
}

.resizer:hover { background: rgba(255, 114, 34, 0.08); }

.resizer-handle {
  width: 4px;
  height: 48px;
  border-radius: 4px;
  background: #d0d0d0;
  transition: background 0.15s;
}

.resizer:hover .resizer-handle { background: #ff7222; }

.section-header {
  border-bottom: 2px solid #ff7222;
  padding-bottom: 8px;
  flex-shrink: 0;
}

.section-subtitle {
  color: #ff7222;
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
}

/* ── Video ── */
.video-container {
  background: #1a1a1a;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
}

.webcam-container {
  aspect-ratio: 4 / 3;
  height: auto;
}

.video-frame {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.placeholder-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #888;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.05);
}

/* ── Timeline ── */
.timeline-container {
  background: #fdfdfd;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px 16px;
  flex-shrink: 0;
}

.timeline-track-wrapper {
  position: relative;
  padding-top: 6px;
}

.timeline-slider {
  width: 100%;
  accent-color: #ff7222;
  cursor: pointer;
  position: relative;
  z-index: 5;
  margin: 0;
}

.time-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 6px;
}

.visual-timeline {
  position: relative;
  height: 44px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  margin-top: 8px;
  background: #fafafa;
  transition: border-color 0.2s;
}

.visual-timeline:hover { border-color: #ff7222; }

.drop-hint {
  width: 100%;
  text-align: center;
  color: #aaa;
  font-size: 0.75rem;
  margin-top: 12px;
}

/* ── Markers ── */
.timeline-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 114, 34, 0.2);
  cursor: pointer;
}

.timeline-marker:hover {
  background: #ff7222;
  z-index: 10;
}

.marker-pin {
  width: 10px;
  height: 10px;
  background: #ff7222;
  border: 2px solid white;
  border-radius: 50%;
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.pin-bug { background: #db3360; }

.marker-tooltip {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 20;
}

.timeline-marker:hover .marker-tooltip { opacity: 1; }

/* ── Recording actions ── */
.recording-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.action-button {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  color: white;
  transition: 0.2s;
}

.btn-start { background: linear-gradient(135deg, #ff7222, #ff8e4d); }
.btn-stop  { background: #db3360; }
.btn-stop:disabled { background: #d7cbc5; cursor: not-allowed; }

/* ── Tabs ── */
.tab-bar {
  display: flex;
  background: #f3f3f3;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
}

.tab-btn {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
}

.tab-active {
  background: #ff7222 !important;
  color: white !important;
}

.tab-content {
  flex: 1;
  min-height: 0;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}

.drag-item {
  background: #fff3ea;
  border: 1px solid #ffe0cc;
  border-radius: 8px;
  padding: 10px;
  cursor: grab;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.drag-item-required{
  background: #d2557643;
  border: 1px solid #db3360;
}

.item-title { font-weight: bold; color: #ff7222; font-size: 0.85rem; }
.item-body  { font-size: 0.8rem; color: #666; }

.bug-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-bottom: 4px;
  box-sizing: border-box;
}

.bug-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
  flex-shrink: 0;
}

.btn-add    { background: #17a2b8; font-size: 0.75rem; }
.btn-remove { background: #6c757d; font-size: 0.75rem; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .main-container {
    flex-direction: column;
    overflow-y: auto;
  }

  .resizer {
    width: 100%;
    height: 12px;
    cursor: row-resize;
  }

  .resizer-handle {
    width: 48px;
    height: 4px;
  }

  .recording-card {
    flex: none;
    width: 100% !important;
    max-width: 100% !important;
  }

  .drag-drop-card {
    flex: none;
    width: 100%;
    height: 400px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/playtest/Playtest-Main.vue